import React, { memo, useCallback, type ComponentType } from 'react';
import { styled as styled2 } from '@compiled/react';
import { Box, xcss } from '@atlaskit/primitives';
import {
	type ChangeEvent,
	ChangeEventTypes,
} from '@atlassian/jira-issue-view-model/src/change-type.tsx';
import type { ExternalAction } from '@atlassian/jira-issue-view-store/src/actions/external-actions.tsx';
import { JiraDetailPanel } from '@atlassian/jira-layout-controller/src/ui/detail-panel/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { useShouldRefetchEpicPanel } from '@atlassian/jira-software-backlog-panel/src/services/epic-panel-refetch/index.tsx';
import { useQueryParam } from '@atlassian/jira-software-router-utils/src/services/query-param/index.tsx';
import { isViewSettingAsPanelExpEnabledWithNoExposure } from '../../feature-flags.tsx';
import { QUERY_PARAM_SELECTED_ISSUE } from '../../model/routing/constants.tsx';
import type { CardKey } from '../../model/types.tsx';
import type { CloseDetailViewAction } from '../../state/actions/detail-view/index.tsx';
import { useIsCMPBacklog } from '../../state/state-hooks/capabilities.tsx';
import { useIsInsightsPanelOpen } from '../../state/state-hooks/use-is-insights-panel-open.tsx';
import { useIsViewSettingsPanelOpen } from '../../state/state-hooks/use-is-view-settings-panel-open.tsx';
import { AsyncBacklogInsightsPanel } from '../backlog-content/insights-panel/async.tsx';
import type { Props as IssueWrapperProps } from './issue-wrapper/index.tsx';
import { Resizer } from './resizer/view.tsx';
import { ViewSettingsPanel } from './view-settings-panel/index.tsx';

export type IssueDeleteArgs = { issueId: string };

export type Props = {
	isBacklogWithSprints: boolean;
	IssueWrapper: ComponentType<IssueWrapperProps>;
	cardKey: CardKey | null;
	rapidViewId: string;
	parentWidth: number;
	timestamp: number; // it is used in shallow comparison,
	backlogSessionId: string;
	externalAction: ExternalAction | undefined;
	onClose: () => CloseDetailViewAction;
	onChange: (event: ChangeEvent) => void;
	onDelete: (arg1: IssueDeleteArgs) => void;
	onCloseInsightsClick: () => void;
	onCloseViewSettingsPanelClick: () => void;
};

const REFETCH_EPIC_PANELS_EVENTS: string[] = [
	ChangeEventTypes.FIELD_CHANGED,
	ChangeEventTypes.ISSUE_RELATIONSHIP_UPDATED,
	ChangeEventTypes.CHILD_ISSUE_ADDED,
	ChangeEventTypes.ISSUE_TYPE_CHANGED,
];

export const DetailView = ({
	parentWidth,
	cardKey,
	rapidViewId,
	onClose,
	onChange,
	onDelete,
	timestamp,
	onCloseInsightsClick,
	onCloseViewSettingsPanelClick,
	isBacklogWithSprints,
	externalAction,
	backlogSessionId,
	IssueWrapper,
}: Props) => {
	const [issueKey, setIssueKey] = useQueryParam(QUERY_PARAM_SELECTED_ISSUE);
	const issueKeyOrCardKey = issueKey || cardKey;
	const onIssueKeyChange = useCallback(
		({ toIssueKey }: { toIssueKey: string | null | undefined }) => setIssueKey(toIssueKey),
		[setIssueKey],
	);

	const isInsightsOpen = useIsInsightsPanelOpen();

	const isViewSettingsPanelOpen = useIsViewSettingsPanelOpen();

	const isCompanyManaged = useIsCMPBacklog();
	const [_, { triggerEpicPanelRefetch }] = useShouldRefetchEpicPanel();

	const handleChange = useCallback(
		(event: ChangeEvent) => {
			onChange(event);
			if (isCompanyManaged && REFETCH_EPIC_PANELS_EVENTS.includes(event.type)) {
				triggerEpicPanelRefetch();
			}
		},
		[isCompanyManaged, onChange, triggerEpicPanelRefetch],
	);

	const handleIssueDelete = useCallback(
		(data: IssueDeleteArgs) => {
			onDelete(data);
			if (isCompanyManaged) {
				triggerEpicPanelRefetch();
			}
		},
		[isCompanyManaged, onDelete, triggerEpicPanelRefetch],
	);

	const getBacklogInsightsPanel = useCallback(
		() =>
			isInsightsOpen ? (
				<DetailsPanelContainer>
					<div>
						<Placeholder name="backlog-insights-panel" fallback={<></>}>
							<AsyncBacklogInsightsPanel isOpen={isInsightsOpen} onClose={onCloseInsightsClick} />
						</Placeholder>
					</div>
				</DetailsPanelContainer>
			) : null,
		[isInsightsOpen, onCloseInsightsClick],
	);

	const getBacklogViewSettingsPanel = useCallback(
		() =>
			isViewSettingsPanelOpen ? (
				<Box xcss={ViewSettingsPanelContainerStyles}>
					<ViewSettingsPanel
						isPanelOpen={isViewSettingsPanelOpen}
						onPanelClose={onCloseViewSettingsPanelClick}
					/>
				</Box>
			) : null,
		[isViewSettingsPanelOpen, onCloseViewSettingsPanelClick],
	);

	return isViewSettingAsPanelExpEnabledWithNoExposure() && isViewSettingsPanelOpen ? (
		getBacklogViewSettingsPanel()
	) : (
		<JiraDetailPanel>
			{(setDetailPanelWidth) => (
				<Resizer parentWidth={parentWidth} onResize={setDetailPanelWidth}>
					{issueKeyOrCardKey !== null && issueKeyOrCardKey !== undefined && (
						<DetailsWrapper hide={isInsightsOpen}>
							<IssueWrapper
								key={issueKeyOrCardKey + timestamp} // Forces a complete re-render rather than an update
								issueKey={issueKeyOrCardKey}
								onClose={onClose}
								onChange={handleChange}
								onIssueDeleteSuccess={handleIssueDelete}
								rapidViewId={Number(rapidViewId)}
								isAgilityBoard
								isLoadedWithPage
								onIssueKeyChange={onIssueKeyChange}
								history={null}
								externalAction={externalAction}
								backlogSessionId={backlogSessionId}
							/>
						</DetailsWrapper>
					)}
					{isBacklogWithSprints && getBacklogInsightsPanel()}
				</Resizer>
			)}
		</JiraDetailPanel>
	);
};

export default memo<Props>(DetailView);

const ViewSettingsPanelContainerStyles = xcss({
	overflowX: 'hidden',
	overflowY: 'auto',
	paddingRight: 'space.500',
	paddingLeft: 'space.300',
	paddingBottom: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DetailsWrapper = styled2.div<{ hide?: boolean }>({
	flex: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	display: (props) => (props.hide ? 'none' : undefined),
	height: '100%',
	overflowX: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DetailsPanelContainer = styled2.div({
	flex: 1,
	position: 'relative',
	height: '100%',
	overflowX: 'hidden',
	overflowY: 'scroll',
});
